import { useState } from "react";
import { useCaseCampaign, type CampaignStage, type CampaignDeliverable } from "../../hooks/useCaseCampaign";

/**
 * The Campaign — the case's procedural war plan, driven entirely by the
 * jurisdiction ruleset (stages, deliverables, clocks all come from data, so
 * other jurisdictions swap in without code changes).
 */
export default function CampaignRail({ caseId }: { caseId: string | undefined }) {
  const { data, isLoading, error } = useCaseCampaign(caseId);
  const [openStage, setOpenStage] = useState<string | null>(null);
  const [showClocks, setShowClocks] = useState(false);

  if (isLoading) return <p className="font-display text-base text-bone-mute">// drawing the campaign map…</p>;
  if (error) return <p className="font-display text-base text-ember-bright">// {error.message}</p>;
  if (!data?.ruleset) return null;

  return (
    <div className="rounded border border-steel-dark bg-iron/70 backdrop-blur-sm">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-steel-dark/70 px-5 py-3">
        <span className="font-display text-base font-light uppercase tracking-[0.16em] text-bone-dim">
          the campaign // {data.ruleset.name}
        </span>
        <div className="flex items-center gap-3">
          <span className="font-display text-sm font-light uppercase tracking-[0.08em] text-bone-mute/70">{data.ruleset.version}</span>
          <button type="button" onClick={() => setShowClocks((s) => !s)}
            className="font-display text-sm font-light uppercase tracking-[0.1em] text-blue-bright/80 hover:text-blue-bright">
            {showClocks ? "▾ hide clocks" : "▸ the clocks"}
          </button>
        </div>
      </div>

      {showClocks && (
        <div className="grid grid-cols-1 gap-1.5 border-b border-steel-dark/70 bg-black/20 px-5 py-3 sm:grid-cols-2 xl:grid-cols-3">
          {data.params.map((p) => (
            <div key={p.key} className="rounded border border-steel-dark/50 bg-iron/40 px-2.5 py-1.5">
              <p className="font-display text-sm font-light uppercase tracking-[0.14em] text-bone-mute">{p.cite ?? ""}</p>
              <p className="font-display text-base leading-snug text-bone-dim">
                {p.label}: <span className="text-bone">{p.value}{p.unit ? ` ${p.unit}` : ""}</span>
              </p>
              {p.notes && <p className="font-display text-base italic text-bone-mute/70">{p.notes}</p>}
            </div>
          ))}
        </div>
      )}

      <div className="px-5 py-4">
        <ol className="space-y-1.5">
          {data.stages.map((s) => (
            <StageRow key={s.key} s={s} open={openStage === s.key}
              onToggle={() => setOpenStage(openStage === s.key ? null : s.key)} />
          ))}
        </ol>
        {data.ruleset.notes && (
          <p className="mt-3 font-display text-sm font-light uppercase tracking-[0.08em] text-bone-mute/60">// {data.ruleset.notes}</p>
        )}
      </div>
    </div>
  );
}

function StageRow({ s, open, onToggle }: { s: CampaignStage; open: boolean; onToggle: () => void }) {
  const forged = s.deliverables.filter((d) => (d.forged_count ?? 0) > 0).length;
  return (
    <li className="overflow-hidden rounded border border-steel-dark/60 bg-black/20">
      <button type="button" onClick={onToggle} className="flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-iron-2/40">
        <span className="font-display text-base font-light text-bone-mute">{s.ordinal}</span>
        <span className="min-w-0 flex-1">
          <span className="block font-display text-[13px] font-medium text-bone">{s.name}</span>
          {s.trigger && <span className="block font-display text-sm font-light uppercase tracking-[0.14em] text-bone-mute/70">clock: {s.trigger}</span>}
        </span>
        <span className="flex shrink-0 items-center gap-1.5">
          {s.deliverables.map((d) => <DelivDot key={d.key} d={d} />)}
        </span>
        <span className="shrink-0 font-display text-sm text-bone-mute">{forged}/{s.deliverables.length} forged {open ? "▾" : "▸"}</span>
      </button>
      {open && (
        <div className="border-t border-steel-dark/60 px-4 py-3">
          {s.description && <p className="font-display text-[12px] leading-snug text-bone-dim">{s.description}</p>}
          <ul className="mt-2 space-y-1.5">
            {s.deliverables.map((d) => (
              <li key={d.key} className="rounded border border-steel-dark/50 bg-iron/40 px-3 py-2">
                <div className="flex flex-wrap items-center gap-2">
                  <DelivDot d={d} />
                  <span className="font-display text-[12px] text-bone">{d.name}</span>
                  <span className="font-display text-sm font-light uppercase tracking-[0.14em] text-bone-mute">
                    {(d.forged_count ?? 0) > 0 ? `forged ×${d.forged_count}` : d.status_hint}
                  </span>
                </div>
                {d.description && <p className="mt-0.5 font-display text-base leading-snug text-bone-mute">{d.description}</p>}
                {d.constraint_note && <p className="mt-0.5 font-display text-sm font-light uppercase tracking-[0.12em] text-ember-soft">⚠ {d.constraint_note}</p>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </li>
  );
}

function DelivDot({ d }: { d: CampaignDeliverable }) {
  const forged = (d.forged_count ?? 0) > 0;
  const color = forged ? "#5a8a5a" : d.status_hint === "available" ? "#e8703a" : "#5a6470";
  const glyph = forged ? "✓" : d.status_hint === "available" ? "⚒" : "◌";
  return (
    <span title={`${d.name} — ${forged ? "forged" : d.status_hint}`}
      className="inline-flex h-4 w-4 items-center justify-center rounded-full border text-sm"
      style={{ borderColor: color, color }}>
      {glyph}
    </span>
  );
}
